import { useState, useEffect } from 'react'
import { usePortalStore } from '@/stores/portal.store'
import { supabase } from '@/config/supabase'
import { 
    Heart, 
    History, 
    Gift, 
    Trophy, 
    Sparkles, 
    ArrowRight,
    Loader2,
    CheckCircle2,
    AlertCircle
} from 'lucide-react'
import { formatDate, formatCurrency } from '@/lib/formatters'
import { useQuery } from '@tanstack/react-query'
import { useAllServicos } from '@/hooks/useServicos'
import { useNavigate } from 'react-router-dom'

export function PortalPontosPage() {
    const associado = usePortalStore(s => s.associado)
    const rawCpf = usePortalStore(s => s.cpf) || associado?.cpf
    const navigate = useNavigate()
    const { data: todosServicos } = useAllServicos()
    const servicosComPontos = (todosServicos || []).filter(s => s.pontos_adesao && s.pontos_adesao > 0 && s.visivel_portal)
    
    // Leitura DIRETA do banco (Bypassing Edge Function for stability)
    const { data: realExtrato, isLoading: loadingExtrato, refetch: refetchExtrato } = useQuery({
        queryKey: ['portal_pontos_extrato', associado?.id],
        queryFn: async () => {
            if (!associado?.id) return []
            const { data, error } = await supabase
                .schema('veicular_system')
                .from('veic_pontos_extrato')
                .select('*')
                .eq('associado_id', associado.id)
                .order('created_at', { ascending: false })
            
            if (error) throw error
            return data
        },
        enabled: !!associado?.id
    })

    const { data: saldoTotal, refetch: refetchSaldo } = useQuery({
        queryKey: ['portal_pontos_saldo', associado?.id],
        queryFn: async () => {
            if (!associado?.id) return 0
            const { data, error } = await supabase
                .schema('veicular_system')
                .rpc('get_saldo_pontos_associado', { p_associado_id: associado.id })
            
            if (error) throw error
            return data || 0
        },
        enabled: !!associado?.id
    })

    const [sjDisponivel, setSjDisponivel] = useState(false)
    const [sjLoading, setSjLoading] = useState(true)
    const [sjCheckingIn, setSjCheckingIn] = useState(false)

    useEffect(() => {
        checkDisponibilidade()
    }, [realExtrato])

    const checkDisponibilidade = () => {
        if (!realExtrato) return
        const ultimosCheckins = realExtrato.filter((i: any) => i.tipo === 'checkin')
        if (ultimosCheckins.length === 0) {
            setSjDisponivel(true)
        } else {
            const lastDate = new Date(ultimosCheckins[0].created_at)
            const diffMs = new Date().getTime() - lastDate.getTime()
            const diffDays = diffMs / (1000 * 60 * 60 * 24)
            setSjDisponivel(diffDays >= 30) // Frequência padrão de 30 dias
        }
        setSjLoading(false)
    }

    const [showSuccessModal, setShowSuccessModal] = useState(false)

    const handleSempreJuntosCheckin = async () => {
        if (!associado?.id || sjCheckingIn) return
        setSjCheckingIn(true)
        try {
            const { data, error } = await supabase
                .schema('veicular_system')
                .rpc('fazer_checkin_fidelidade', { p_associado_id: associado.id })

            if (error) throw error
            
            if (data?.success === false) {
                alert(data.message)
            } else if (data?.success === true) {
                refetchSaldo()
                refetchExtrato()
                setShowSuccessModal(true)
            }
        } catch (error) {
            console.error('Erro no checkin', error)
            alert('Não foi possível processar o check-in. Tente novamente.')
        } finally {
            setSjCheckingIn(false)
        }
    }


    return (
        <div className="p-5 space-y-6 animate-fade-in max-w-lg mx-auto pb-10">
            {/* Header / Hero */}
            <div className="pt-4 flex flex-col items-center text-center space-y-2">
                <div className="w-16 h-16 bg-rose-50 rounded-3xl flex items-center justify-center text-rose-500 shadow-sm border border-rose-100">
                    <Heart size={32} className="fill-current animate-pulse-subtle" />
                </div>
                <div>
                    <h1 className="text-2xl font-black text-[#061C37] tracking-tight">Sempre Juntos</h1>
                    <p className="text-gray-400 text-sm font-medium">Sua fidelidade recompensada</p>
                </div>
            </div>

            {/* Score Card */}
            <div className="p-8 rounded-[2.5rem] bg-gradient-to-br from-pink-500/90 to-rose-600 text-white shadow-2xl shadow-rose-500/30 relative overflow-hidden text-center">
                <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full blur-3xl -mr-10 -mt-10" />
                <div className="absolute bottom-0 left-0 w-24 h-24 bg-rose-900/10 rounded-full blur-2xl -ml-5 -mb-5" />
                
                <p className="text-[10px] font-black uppercase tracking-[0.3em] opacity-80 mb-2">Saldo Atual</p>
                <div className="flex items-center justify-center gap-2">
                    <Trophy size={28} className="text-amber-300 drop-shadow-[0_2px_4px_rgba(0,0,0,0.2)]" />
                    <span className="text-6xl font-black tracking-tighter drop-shadow-md">{saldoTotal || 0}</span>
                </div>
                <p className="text-xs font-bold opacity-70 mt-3">Pontos acumulados para brindes</p>
            </div>

            {/* Check-in Action */}
            <div className="glass-card p-6 rounded-[2rem] border-rose-100 bg-white/50 space-y-4">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-2xl bg-rose-50 flex items-center justify-center text-rose-500">
                        <Sparkles size={20} />
                    </div>
                    <div>
                        <p className="text-sm font-black text-[#061C37]">Check-in de Fidelidade</p>
                        <p className="text-[10px] font-bold text-gray-400">Clique para coletar seus pontos</p>
                    </div>
                </div>

                {sjLoading ? (
                    <div className="h-14 bg-gray-50 rounded-2xl animate-pulse flex items-center justify-center">
                        <Loader2 className="animate-spin text-gray-300" size={24} />
                    </div>
                ) : sjDisponivel ? (
                    <button
                        onClick={handleSempreJuntosCheckin}
                        disabled={sjCheckingIn}
                        className="w-full py-4 bg-[#003E8A] text-white font-black rounded-2xl shadow-lg 
                            hover:scale-[1.02] active:scale-[0.98] transition-all flex items-center justify-center gap-2"
                    >
                        {sjCheckingIn ? <Loader2 className="animate-spin" /> : <>Ganhar +10 Pontos <ArrowRight size={18} /></>}
                    </button>
                ) : (
                    <div className="p-4 rounded-2xl bg-green-50 border border-green-100 flex items-center gap-3">
                        <CheckCircle2 size={24} className="text-green-500" />
                        <p className="text-[11px] font-bold text-green-700 leading-tight">
                            Você já garantiu seus pontos! Volte em breve para novos resgates.
                        </p>
                    </div>
                )}
            </div>

            {/* Extrato / History */}
            <div className="space-y-4">
                <div className="flex items-center justify-between px-1">
                    <p className="text-xs font-black text-gray-400 uppercase tracking-[0.2em] flex items-center gap-2">
                        <History size={14} /> Histórico de Pontos
                    </p>
                </div>

                {loadingExtrato ? (
                    <div className="space-y-3">
                        {[1,2,3].map(i => <div key={i} className="h-16 bg-gray-50 rounded-2xl animate-pulse" />)}
                    </div>
                ) : realExtrato?.length === 0 ? (
                    <div className="py-10 text-center space-y-3">
                        <div className="w-12 h-12 bg-gray-50 rounded-full flex items-center justify-center mx-auto text-gray-300">
                            <AlertCircle size={24} />
                        </div>
                        <p className="text-xs font-bold text-gray-400">Nenhum ponto registrado ainda.</p>
                    </div>
                ) : (
                    <div className="space-y-2">
                        {realExtrato?.map(item => (
                            <div key={item.id} className="flex items-center justify-between p-4 rounded-2xl bg-white border border-gray-100 shadow-sm">
                                <div className="flex items-center gap-3">
                                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                                        item.pontos > 0 ? 'bg-green-50 text-green-600' : 'bg-rose-50 text-rose-600'
                                    }`}>
                                        <Heart size={18} className={item.pontos > 0 ? 'fill-current' : ''} />
                                    </div>
                                    <div>
                                        <p className="text-sm font-bold text-[#061C37]">{item.descricao}</p>
                                        <p className="text-xs text-gray-400">{formatDate(item.created_at)}</p>
                                    </div>
                                </div>
                                <div className={`font-black text-sm ${item.pontos > 0 ? 'text-green-600' : 'text-rose-600'}`}>
                                    {item.pontos > 0 ? '+' : ''}{item.pontos}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Serviços que dão pontos */}
            {servicosComPontos.length > 0 && (
                <div className="space-y-3">
                    <p className="text-xs font-black text-gray-400 uppercase tracking-[0.2em] flex items-center gap-2 px-1">
                        <Gift size={14} /> Ganhe mais pontos
                    </p>
                    <div className="space-y-2">
                        {servicosComPontos.map(s => (
                            <button
                                key={s.id}
                                onClick={() => navigate('/portal/loja')}
                                className="w-full flex items-center justify-between p-4 rounded-2xl bg-white border border-gray-100 shadow-sm hover:shadow-md hover:border-rose-100 transition-all text-left group"
                            >
                                <div className="flex items-center gap-3 min-w-0">
                                    <div className="w-10 h-10 rounded-xl bg-rose-50 text-rose-500 flex items-center justify-center flex-shrink-0">
                                        <Sparkles size={18} />
                                    </div>
                                    <div className="min-w-0">
                                        <p className="text-sm font-bold text-[#061C37] truncate">{s.nome}</p>
                                        <p className="text-xs text-gray-400">{formatCurrency(s.valor_mensal)}/mês</p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2 flex-shrink-0">
                                    <span className="px-3 py-1.5 rounded-full bg-rose-50 text-rose-600 text-xs font-black border border-rose-100">
                                        +{s.pontos_adesao} PTS
                                    </span>
                                    <ArrowRight size={14} className="text-gray-300 group-hover:text-rose-400 transition-colors" />
                                </div>
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {/* Próximos Brindes */}
            <div className="p-6 rounded-[2rem] bg-[#061C37] text-white relative overflow-hidden group border border-white/10">
                <div className="relative z-10 flex items-center justify-between">
                    <div className="space-y-0.5">
                        <p className="text-[10px] font-black text-rose-400 uppercase tracking-widest">Em Breve</p>
                        <h4 className="font-black text-lg">Trocar por Brindes</h4>
                        <p className="text-[11px] text-white/50 font-bold">Resgate produtos exclusivos na nossa loja usando seu saldo.</p>
                    </div>
                    <div className="w-12 h-12 bg-white/10 rounded-2xl flex items-center justify-center">
                        <Gift size={24} />
                    </div>
                </div>
            </div>

            {/* Success Modal (Festa Comedida) */}
            {showSuccessModal && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center bg-[#061C37]/80 backdrop-blur-md p-6 animate-fade-in">
                    <div className="bg-white w-full max-w-sm rounded-[3rem] p-10 text-center shadow-2xl animate-scale-in relative overflow-hidden">
                        {/* Decorative Background Elements */}
                        <div className="absolute -top-10 -right-10 w-32 h-32 bg-rose-50 rounded-full blur-2xl" />
                        <div className="absolute -bottom-10 -left-10 w-24 h-24 bg-rose-50 rounded-full blur-2xl" />
                        
                        <div className="relative z-10">
                            <div className="w-24 h-24 bg-rose-50 text-rose-500 rounded-[2.5rem] flex items-center justify-center mx-auto mb-8 shadow-inner border border-rose-100/50">
                                <Trophy size={48} className="animate-bounce-subtle" />
                            </div>
                            
                            <div className="space-y-2 mb-8">
                                <h3 className="text-3xl font-black text-[#061C37] leading-tight">Parabéns! 🎉</h3>
                                <p className="text-gray-500 font-bold text-sm">
                                    Você acaba de ganhar 10 pontos por sua fidelidade.
                                </p>
                            </div>
                            
                            <div className="bg-rose-500/5 py-4 px-6 rounded-3xl border border-rose-100 mb-10">
                                <div className="flex items-center justify-center gap-2">
                                    <Sparkles size={18} className="text-rose-500" />
                                    <span className="text-rose-600 font-black text-xl">+10 PONTOS</span>
                                </div>
                            </div>
                            
                            <button
                                onClick={() => setShowSuccessModal(false)}
                                className="w-full py-5 bg-[#061C37] text-white font-black rounded-3xl shadow-xl 
                                    hover:scale-[1.02] active:scale-[0.98] transition-all"
                            >
                                Continuar
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}
